/**
 * CartDrawer — Luxury Fashion Cart Drawer
 * Handles open/close, AJAX cart updates, section rendering refresh
 * Shopify Horizon OS 2.0 compatible
 *
 * FIXES applied:
 *  1. Cart icon click interception broadened to cover Horizon's <button> cart trigger
 *  2. Focus trap re-queries live DOM on every Tab press (no stale node list)
 *  3. _renderCartHTML() skips always-failing Section Rendering
 *  4. rebuildDrawerFromCart() calls CartRecommendations.init()
 *  5. rebuildDrawerFromCart() resets shipping bar to 0 when cart empties
 *  6. Quick-add resets qty display before refresh; drawer opens only once
 *  7. Size variant pill uses the correct option slot index
 *  8. SET/BUNDLE products: deduplicated unified size selector in _buildCardHTML
 *  9. Fixed card dimensions enforced via CSS; JS carousel step updated to match
 */

(function () {
  'use strict';

  /* -------------------------------------------------------
     Constants
  ------------------------------------------------------- */
  const SELECTORS = {
    drawer:           '#cart-drawer',
    overlay:          '#cart-drawer-overlay',
    close:            '#cart-drawer-close',
    panel:            '.cart-drawer__panel',
    body:             '#cart-drawer-body',
    itemsContainer:   '#cart-items-container',
    footer:           '#cart-drawer-footer',
    subtotalAmount:   '#cart-subtotal-amount',
    shippingBar:      '#cart-shipping-bar',
    progressFill:     '#cart-progress-fill',
    recsWrapper:      '#cart-recommendations-wrapper',
    continueShopping: '#cart-continue-shopping',
    cartIconBubble:   '.cart-count-bubble, [data-cart-count], .cart-item-count',
  };

  // BUG FIX 1: The Liquid template hardcodes 110000 cents as the free-shipping
  // threshold, but the JS fallback was 10000 — meaning almost every cart showed
  // "FREE SHIPPING UNLOCKED". The fallback now matches the Liquid value.
  // Long-term: set window.CartDrawerSettings.freeShippingThreshold in your theme
  // settings so both sides stay in sync without touching source files.
  const FREE_SHIPPING_THRESHOLD = window.CartDrawerSettings?.freeShippingThreshold ?? 135000;

  /* -------------------------------------------------------
     SET PRODUCT DETECTION
     Mirrors the Liquid logic in recommendation-card.liquid.
     A product is a "set" if it has the tag "set"/"bundle",
     its title contains those words, or the API returns
     product.is_set === true (custom metafield).
  ------------------------------------------------------- */
  function isSetProduct(product) {
    if (product.is_set === true) return true;
    const title = (product.title || '').toLowerCase();
    if (title.includes(' set') || title.includes(' bundle')) return true;
    const tags = product.tags || [];
    const tagList = Array.isArray(tags)
      ? tags.map(t => t.toLowerCase())
      : String(tags).split(',').map(t => t.trim().toLowerCase());
    return tagList.includes('set') || tagList.includes('bundle');
  }

  /* -------------------------------------------------------
     COMING SOON PRODUCT DETECTION
     Mirrors the Liquid logic used across the theme (product
     cards, buy-buttons, resource-card, recommendation-card).
     A product is "coming soon" if it carries the tag
     "coming-soon" (case-insensitive).
  ------------------------------------------------------- */
  function isComingSoonProduct(product) {
    const tags = product.tags || [];
    const tagList = Array.isArray(tags)
      ? tags.map(t => t.toLowerCase())
      : String(tags).split(',').map(t => t.trim().toLowerCase());
    return tagList.includes('coming-soon');
  }

  /* -------------------------------------------------------
     CartDrawer Class
  ------------------------------------------------------- */
  class CartDrawer {
    constructor() {
      this.drawer  = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector(SELECTORS.overlay);
      this.close   = this.drawer.querySelector(SELECTORS.close);
      this.panel   = this.drawer.querySelector(SELECTORS.panel);

      this._bindEvents();
      this._listenForCartEvents();
    }

    open() {
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.classList.add('is-open');
      document.body.classList.add('cart-drawer-open');
      this.close?.focus();
      this._trapFocus();
    }

    close_drawer() {
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('cart-drawer-open');
      this._releaseFocus();
    }

    toggle() {
      this.drawer.classList.contains('is-open')
        ? this.close_drawer()
        : this.open();
    }

    _bindEvents() {
      this.close?.addEventListener('click', () => this.close_drawer());
      this.overlay?.addEventListener('click', () => this.close_drawer());

      // CHECKOUT BUTTON: now a plain <a href="/checkout"> in the Liquid template.
      // No JS handler needed — the browser follows the href unconditionally,
      // bypassing any Horizon dialog/drawer event interceptors on <button> elements.

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close_drawer();
        }
      });

      document.addEventListener('click', (e) => {
        if (e.target.closest('#cart-continue-shopping')) {
          this.close_drawer();
        }

        /**
         * Broadened selector to catch Horizon's cart button variants.
         */
        const trigger = e.target.closest(
          '[data-cart-open],' +
          '[href="/cart"],' +
          '[href*="/cart"],' +
          '.cart-link,' +
          '.header__icon--cart,' +
          '[aria-label*="cart" i],' +
          '[data-cart-toggle]'
        );
        if (trigger && !trigger.closest('#cart-drawer') && !trigger.closest('form')) {
          e.preventDefault();
          this.open();
          CartAPI.refresh();
        }
      });

      document.addEventListener('cart:open', () => {
        this.open();
        CartAPI.refresh();
      });
    }

    _listenForCartEvents() {
      // cart:update carries the full cart object — rebuild drawer immediately, no fetch needed
      document.addEventListener('cart:update', (e) => {
        if (e.detail?.cart) {
          this._freshCartTs = Date.now();
          CartUtils.rebuildDrawerFromCart(e.detail.cart);
        }
      });

      // cart:refresh = external signal to re-sync (e.g. back-button, other tabs).
      // Skip it if we just handled a cart:update in the last 500ms to avoid double-fetching.
      document.addEventListener('cart:refresh', () => {
        if (Date.now() - (this._freshCartTs || 0) > 500) {
          CartAPI.refresh(false);
        }
      });

      document.addEventListener('drawer:open', () => {
        this.open();
      });
    }

    _updateDOM(cart) {
      const subtotalEl = document.querySelector(SELECTORS.subtotalAmount);
      if (subtotalEl) {
        subtotalEl.classList.add('is-updating');
        subtotalEl.textContent = CartUtils.formatMoney(cart.total_price) + ' ' + cart.currency;
        requestAnimationFrame(() => subtotalEl.classList.remove('is-updating'));
      }
      CartUtils.updateShippingBar(cart.total_price);
      CartUtils.updateCartIcons(cart.item_count);
    }

    /**
     * Re-query focusable elements live on every Tab key press.
     */
    _trapFocus() {
      this._trapHandler = (e) => {
        if (e.key !== 'Tab') return;

        const focusable = [
          ...(this.panel?.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]'
          ) || [])
        ].filter(el => !el.closest('[hidden]') && el.offsetParent !== null);

        if (!focusable.length) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      document.addEventListener('keydown', this._trapHandler);
    }

    _releaseFocus() {
      if (this._trapHandler) {
        document.removeEventListener('keydown', this._trapHandler);
        this._trapHandler = null;
      }
    }
  }

  /* -------------------------------------------------------
     CartAPI — AJAX Cart Actions
  ------------------------------------------------------- */
  const CartAPI = {
    async refresh(autoOpen = false) {
      const panel = document.querySelector(SELECTORS.panel);
      panel?.classList.add('is-loading');

      try {
        const cartData = await this.getCart();

        /**
         * Skip Section Rendering — snippet has no section wrapper.
         * Go straight to JS rebuild.
         */
        await CartUtils.rebuildDrawerFromCart(cartData);

        if (autoOpen && window.cartDrawerInstance) {
          window.cartDrawerInstance.open();
        }

        CartUtils.updateCartIcons(cartData.item_count);
        CartUtils.updateShippingBar(cartData.total_price);

      } catch (err) {
        console.error('[CartDrawer] refresh error:', err);
      } finally {
        panel?.classList.remove('is-loading');
      }
    },

    async getCart() {
      const res = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to fetch cart');
      return res.json();
    },

    async addItem(variantId, quantity = 1, properties = {}) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, properties })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add item');
      }
      return res.json();
    },

    async updateItem(key, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!res.ok) throw new Error('Failed to update cart');
      return res.json();
    },

    async removeItem(key) {
      return this.updateItem(key, 0);
    }
  };

  /* -------------------------------------------------------
     CartUtils — Helpers
  ------------------------------------------------------- */
  const CartUtils = {
    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      const amount = (cents / 100).toFixed(2);
      return `${window.CartDrawerSettings?.currencySymbol || 'LE'} ${amount}`;
    },

    updateCartIcons(count) {
      document.querySelectorAll('.cart-bubble__text-count, [data-testid="cart-bubble"]').forEach(el => {
        el.textContent = count > 0 ? String(count > 99 ? '99+' : count) : '';
        if (count > 0) {
          el.classList.remove('hidden');
        } else {
          el.classList.add('hidden');
        }
      });

      document.querySelectorAll('.cart-bubble').forEach(bubble => {
        if (count > 0) {
          bubble.classList.remove('visually-hidden');
          if (count <= 99) {
            bubble.setAttribute('data-maintain-ratio', '');
          } else {
            bubble.removeAttribute('data-maintain-ratio');
          }
        } else {
          bubble.classList.add('visually-hidden');
        }
      });

      document.querySelectorAll(
        '.cart-count-bubble, [data-cart-count], .cart-item-count, .CartCount'
      ).forEach(el => {
        el.textContent = count > 0 ? count : '';
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    updateShippingBar(totalPrice) {
      const fill   = document.querySelector(SELECTORS.progressFill);
      const textEl = document.querySelector('.cart-drawer__shipping-text');
      if (!fill) return;

      const remaining = FREE_SHIPPING_THRESHOLD - totalPrice;
      const progress  = Math.min(100, Math.round((totalPrice / FREE_SHIPPING_THRESHOLD) * 100));

      fill.style.width = progress + '%';

      if (textEl) {
        if (remaining > 0) {
          textEl.className = 'cart-drawer__shipping-text';
          textEl.innerHTML = `ADD <strong>${CartUtils.formatMoney(remaining)}</strong> TO UNLOCK <strong>FREE SHIPPING</strong>`;
        } else {
          textEl.className = 'cart-drawer__shipping-text cart-drawer__shipping-text--unlocked';
          textEl.innerHTML = `🎉 YOU'VE UNLOCKED <strong>FREE SHIPPING</strong>`;
        }
      }
    },

    morphDOM(html, cart) {
      const parser    = new DOMParser();
      const newDoc    = parser.parseFromString(html, 'text/html');
      const newItems  = newDoc.querySelector(SELECTORS.itemsContainer);
      const newFooter = newDoc.querySelector(SELECTORS.footer);
      const newRecs   = newDoc.querySelector(SELECTORS.recsWrapper);
      const newShip   = newDoc.querySelector(SELECTORS.shippingBar);

      if (newItems)  CartUtils.replaceEl(SELECTORS.itemsContainer, newItems);
      if (newFooter) CartUtils.replaceEl(SELECTORS.footer, newFooter);
      if (newRecs)   CartUtils.replaceEl(SELECTORS.recsWrapper, newRecs);
      if (newShip)   CartUtils.replaceEl(SELECTORS.shippingBar, newShip);

      CartQuantitySelector.init();
      CartRecommendations.init();
    },

    replaceEl(selector, newEl) {
      const existing = document.querySelector(selector);
      if (existing && newEl) {
        existing.replaceWith(newEl.cloneNode(true));
      }
    },

    async rebuildDrawerFromCart(cart) {
      const itemsContainer = document.querySelector(SELECTORS.itemsContainer);
      // Re-query footer + recs each time — they may not exist yet if the page
      // loaded with an empty cart (Liquid only stamped them when item_count > 0
      // in older builds; now they're always in the DOM but hidden).
      let footerEl   = document.querySelector(SELECTORS.footer);
      let recsWrapper = document.querySelector(SELECTORS.recsWrapper);

      // ── Ensure footer exists ────────────────────────────────────────────────
      // If Liquid didn't render it (old cached theme output), create it now so
      // the first add-to-cart from an empty cart shows the checkout button.
   if (!footerEl) {
        const panel = document.querySelector(SELECTORS.panel);
        if (panel) {
          const paymentIconsHTML = `
            <ul class="cart-payment-icons" role="list" aria-label="Accepted payment methods">
              <li class="cart-payment-icons__item cart-payment-icons__item--white" aria-label="Apple Pay">
                <svg viewBox="0 0 48 20" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Apple Pay">
                  <title>Apple Pay</title>
                  <g fill="#000000">
                    <path d="M9.2 4.1c-.34.4-.88.72-1.42.68-.07-.55.2-1.13.51-1.49.34-.39.9-.69 1.4-.72.06.57-.15 1.12-.49 1.53zm.5 1.06c-.79-.04-1.46.45-1.85.45-.4 0-1-.42-1.63-.41-.85.01-1.64.49-2.06 1.27-.88 1.5-.23 3.83.65 5.1.43.6.95 1.3 1.6 1.28.64-.02.88-.42 1.66-.42s1 .42 1.66.4c.67-.01 1.13-.6 1.55-1.21.46-.69.66-1.36.67-1.39-.02-.01-1.26-.49-1.26-1.94 0-1.21 1-1.78 1.03-1.8-.52-.79-1.36-.94-1.65-.97z"/>
                    <path d="M16.5 2.6c1.65 0 2.82 1.14 2.82 2.8 0 1.67-1.19 2.82-2.86 2.82h-1.84v2.95h-1.33V2.6h3.21zm-1.88 4.48h1.53c1.15 0 1.78-.6 1.78-1.67s-.63-1.68-1.78-1.68h-1.53v3.35z"/>
                    <path d="M19.66 9.36c0-1.1.85-1.78 2.37-1.86l1.65-.1v-.47c0-.68-.46-1.07-1.23-1.07-.66 0-1.18.33-1.27.84h-1.21c.06-1.11 1.08-1.92 2.52-1.92 1.5 0 2.46.78 2.46 2.01v4.21h-1.23v-1h-.03c-.36.66-1.11 1.07-1.92 1.07-1.2 0-2.11-.72-2.11-1.71zm4.02-.54v-.48l-1.47.09c-.78.06-1.18.34-1.18.85 0 .51.42.84 1.08.84.86 0 1.57-.6 1.57-1.3z"/>
                    <path d="M26.18 12.49v-1.02c.09.03.3.03.42.03.57 0 .87-.24 1.05-.85 0-.03.09-.33.09-.33l-2.16-5.95h1.38l1.5 4.8h.03l1.5-4.8h1.35l-2.25 6.27c-.51 1.44-1.11 1.93-2.34 1.93-.1-.01-.41-.04-.57-.08z"/>
                  </g>
                </svg>
              </li>
              <li class="cart-payment-icons__item cart-payment-icons__item--black" aria-label="Mastercard">
                <svg viewBox="0 0 48 20" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Mastercard">
                  <title>Mastercard</title>
                  <circle cx="20" cy="10" r="6.4" fill="#EB001B"/>
                  <circle cx="28" cy="10" r="6.4" fill="#F79E1B"/>
                  <path fill="#FF5F00" d="M24 5.1a6.4 6.4 0 0 0 0 9.8 6.4 6.4 0 0 0 0-9.8z"/>
                </svg>
              </li>
              <li class="cart-payment-icons__item cart-payment-icons__item--blue" aria-label="Visa">
                <svg viewBox="0 0 48 20" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Visa">
                  <title>Visa</title>
                  <text x="50%" y="58%" text-anchor="middle" dominant-baseline="middle" font-family="Helvetica, Arial, sans-serif" font-size="13" font-weight="700" font-style="italic" fill="#FFFFFF" letter-spacing="0.4">VISA</text>
                </svg>
              </li>
            </ul>`;

          footerEl = document.createElement('div');
          footerEl.className = 'cart-drawer__footer';
          footerEl.id = 'cart-drawer-footer';
          footerEl.innerHTML = `
            <div class="cart-drawer__subtotal-row">
              <span class="cart-drawer__subtotal-label">ESTIMATED TOTAL</span>
              <span class="cart-drawer__subtotal-amount" id="cart-subtotal-amount"></span>
            </div>
            ${paymentIconsHTML}
            <form action="/checkout" method="post" id="cart-checkout-form">
              <button type="submit" class="cart-drawer__checkout-btn" id="cart-checkout-btn">
                CHECK OUT
              </button>
            </form>`;
          panel.appendChild(footerEl);
        }
      }

      // ── Ensure recs wrapper exists ──────────────────────────────────────────
      if (!recsWrapper) {
        const body = document.querySelector(SELECTORS.body);
        if (body) {
          recsWrapper = document.createElement('div');
          recsWrapper.className = 'cart-drawer__recommendations';
          recsWrapper.id = 'cart-recommendations-wrapper';
          // Inner #cart-recommendations node is created by CartRecommendations.init()
          body.appendChild(recsWrapper);
        }
      }

      if (itemsContainer) {
        if (cart.item_count === 0) {
          itemsContainer.innerHTML = `
            <div class="cart-drawer__empty">
              <p class="cart-drawer__empty-text">YOUR CART IS EMPTY</p>
              <button class="cart-drawer__continue-btn" id="cart-continue-shopping" type="button">
                CONTINUE SHOPPING
              </button>
            </div>`;
          if (footerEl) footerEl.style.display = 'none';
          if (recsWrapper) recsWrapper.style.display = 'none';
          CartUtils.updateShippingBar(0);
        } else {
          itemsContainer.innerHTML = cart.items.map(item => CartUtils._buildItemHTML(item)).join('');
          if (footerEl) footerEl.style.display = '';
          if (recsWrapper) recsWrapper.style.display = '';
        }
      }

      const subtotalEl = document.querySelector(SELECTORS.subtotalAmount);
      if (subtotalEl) {
        subtotalEl.textContent = CartUtils.formatMoney(cart.total_price) + ' ' + cart.currency;
      }
      CartUtils.updateCartIcons(cart.item_count);
      CartUtils.updateShippingBar(cart.total_price);
      CartQuantitySelector.init();

      // BUG FIX (recs — Bug 2): The recs wrapper innerHTML is NOT destroyed on
      // rebuild, but the inner #cart-recommendations node may have been removed
      // if recsWrapper was re-created from scratch (first add-to-cart from an
      // empty cart). In that case _cache still holds the old entry, so init()
      // would bail out early thinking recs are already loaded — leaving the
      // section blank. We clear the cache for the current seed product before
      // calling init() so a fresh fetch/render always happens when the node is gone.
      const seedProductId = String(
        cart.items?.[0]?.product_id || ''
      );
      if (seedProductId && recsWrapper && !recsWrapper.querySelector('[data-rec-card]')) {
        delete CartRecommendations._cache[seedProductId];
      }
      // Pass the product ID directly — do not rely on DOM scraping of [data-cart-item]
      // because Liquid-rendered items may not always have data-product-id stamped.
      CartRecommendations.init(seedProductId || null);
    },

    _buildItemHTML(item) {
      // BUG FIX 3: item.image from /cart.js is the master (full-resolution) URL.
      // Shopify accepts a _WIDTHx suffix before the extension to serve a resized
      // image, e.g. product_abc.jpg → product_abc_200x.jpg. The Liquid template
      // already uses `| image_url: width: 200`; we replicate that here so AJAX
      // rebuilds don't load multi-MB originals on every cart update.
      const resizedImage = item.image
        ? item.image.replace(/(\.(?:jpe?g|png|gif|webp))(\?.*)?$/i, '_200x$1$2')
        : null;

      const imgHTML = resizedImage
        ? `<img class="cart-item__image" src="${resizedImage}" alt="${item.product_title || ''}" loading="lazy" width="200">`
        : `<div class="cart-item__image-placeholder"></div>`;

      const variantMeta = (item.variant_options || [])
        .map(opt => `<span class="cart-item__variant-line">${opt.toUpperCase()}</span>`)
        .join('');

      const priceHTML = item.original_line_price !== item.final_line_price
        ? `<s class="cart-item__price cart-item__price--original">${CartUtils.formatMoney(item.original_price)}</s>
           <span class="cart-item__price cart-item__price--sale">${CartUtils.formatMoney(item.final_price)}</span>`
        : `<span class="cart-item__price">${CartUtils.formatMoney(item.final_price)}</span>`;

      return `
        <div class="cart-item" data-cart-item data-key="${item.key}" data-variant-id="${item.variant_id}" data-product-id="${item.product_id}" id="cart-item-${item.key}">
          <a href="${item.url}" class="cart-item__image-link" tabindex="-1" aria-hidden="true">
            <div class="cart-item__image-wrap">${imgHTML}</div>
          </a>
          <div class="cart-item__details">
            <div class="cart-item__info-row">
              <div class="cart-item__info">
                <a href="${item.url}" class="cart-item__title">${(item.product_title || '').toUpperCase()}</a>
                <div class="cart-item__price-wrap">${priceHTML}</div>
              </div>
            </div>
            <div class="cart-item__meta">${variantMeta}</div>
            <div class="cart-item__actions-row">
              <div class="cart-item__qty-selector" data-qty-selector data-key="${item.key}">
                <button class="cart-item__qty-btn" type="button" data-action="decrease" aria-label="Decrease quantity">
                  <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true"><line x1="0" y1="1" x2="10" y2="1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
                <input class="cart-item__qty-input" type="number" min="0" max="99" value="${item.quantity}" data-qty-input aria-label="Quantity">
                <button class="cart-item__qty-btn" type="button" data-action="increase" aria-label="Increase quantity">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><line x1="5" y1="0" x2="5" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="0" y1="5" x2="10" y2="5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
              </div>
              <button class="cart-item__remove-btn" type="button" data-remove-item data-key="${item.key}" aria-label="Remove from cart">
                <svg width="14" height="16" viewBox="0 0 14 16" fill="none" aria-hidden="true"><path d="M1 4H13M5 4V2H9V4M2 4L2.7 13.1C2.8 13.6 3.3 14 3.8 14H10.2C10.7 14 11.2 13.6 11.3 13.1L12 4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </div>
        </div>`;
    },

    renderItemsFromCart(cart) {
      const subtotalEl = document.querySelector(SELECTORS.subtotalAmount);
      if (subtotalEl) {
        subtotalEl.textContent =
          CartUtils.formatMoney(cart.total_price) + ' ' + cart.currency;
      }
      CartUtils.updateCartIcons(cart.item_count);
      CartUtils.updateShippingBar(cart.total_price);
      CartQuantitySelector.init();
    }
  };

  /* -------------------------------------------------------
     CartQuantitySelector — Handles +/- buttons & input
  ------------------------------------------------------- */
  const CartQuantitySelector = {
    init() {
      document.querySelectorAll('[data-qty-selector]').forEach(selector => {
        if (selector._cartQtyInited) return;
        selector._cartQtyInited = true;

        const key    = selector.dataset.key;
        const input  = selector.querySelector('[data-qty-input]');
        const decBtn = selector.querySelector('[data-action="decrease"]');
        const incBtn = selector.querySelector('[data-action="increase"]');

        decBtn?.addEventListener('click', () => {
          const val = parseInt(input.value, 10);
          if (val > 1) {
            input.value = val - 1;
            CartQuantitySelector._update(key, val - 1, selector);
          } else if (val === 1) {
            CartQuantitySelector._removeItem(key);
          }
        });

        incBtn?.addEventListener('click', () => {
          const val = parseInt(input.value, 10);
          input.value = val + 1;
          CartQuantitySelector._update(key, val + 1, selector);
        });

        let inputTimer;
        input?.addEventListener('change', () => {
          clearTimeout(inputTimer);
          const val = Math.max(0, parseInt(input.value, 10) || 0);
          input.value = val;
          inputTimer = setTimeout(() => {
            if (val === 0) {
              CartQuantitySelector._removeItem(key);
            } else {
              CartQuantitySelector._update(key, val, selector);
            }
          }, 500);
        });
      });

      document.querySelectorAll('[data-remove-item]').forEach(btn => {
        if (btn._cartRemoveInited) return;
        btn._cartRemoveInited = true;
        btn.addEventListener('click', () => {
          CartQuantitySelector._removeItem(btn.dataset.key);
        });
      });
    },

    async _update(key, quantity, selectorEl) {
      const panel = document.querySelector(SELECTORS.panel);
      panel?.classList.add('is-loading');
      try {
        const cart = await CartAPI.updateItem(key, quantity);
        // Only update totals/icons — don't rebuild items (input already shows new qty)
        CartUtils.renderItemsFromCart(cart);
      } catch (err) {
        console.error('[CartQuantity] update error:', err);
      } finally {
        panel?.classList.remove('is-loading');
      }
    },

    async _removeItem(key) {
      const itemEl = document.querySelector(`[data-key="${key}"][data-cart-item]`);
      if (itemEl) {
        itemEl.classList.add('is-removing');
        await new Promise(resolve => setTimeout(resolve, 220));
      }
      const panel = document.querySelector(SELECTORS.panel);
      panel?.classList.add('is-loading');
      try {
        const cart = await CartAPI.removeItem(key);
        await CartUtils.rebuildDrawerFromCart(cart);
        CartUtils.updateCartIcons(cart.item_count);
        CartUtils.updateShippingBar(cart.total_price);
      } catch (err) {
        console.error('[CartQuantity] remove error:', err);
        itemEl?.classList.remove('is-removing');
      } finally {
        panel?.classList.remove('is-loading');
      }
    }
  };

  /* -------------------------------------------------------
     CartRecommendations — Load + Quick Add
  ------------------------------------------------------- */
  const CartRecommendations = {
    _cache: {},         // productId → true, marks fully loaded products
    _prefetchCache: {}, // productId → Promise, in-flight prefetch requests

    init(forcedProductId) {
      const recsWrapper = document.querySelector(SELECTORS.recsWrapper);
      if (!recsWrapper) return;

      // Resolve the product ID from every available source, in priority order.
      // forcedProductId comes from rebuildDrawerFromCart() which has the full
      // cart API response — most reliable. DOM scraping is a fallback for the
      // case where init() is called at DOMContentLoaded with items already in cart.
      const resolvedId = String(
        forcedProductId ||
        document.querySelector('[data-cart-item]')?.dataset?.productId ||
        ''
      );

      let recsEl = recsWrapper.querySelector('#cart-recommendations');

      if (recsEl) {
        // Self-heal: if the element exists but has an empty data-product-id
        // (left by a previous failed init), patch it now if we have the id.
        if (!recsEl.dataset.productId && resolvedId) {
          recsEl.setAttribute('data-product-id', resolvedId);
        }
      } else {
        // Element doesn't exist yet — create it. If we still don't have an id,
        // fall back to a live cart fetch so we never silently bail out.
        if (!resolvedId) {
          // Async fallback: fetch cart, then re-call init with the real id.
          fetch('/cart.js')
            .then(r => r.json())
            .then(cart => {
              const pid = cart?.items?.[0]?.product_id;
              if (pid) CartRecommendations.init(String(pid));
            })
            .catch(() => {});
          return;
        }

        recsEl = document.createElement('div');
        recsEl.className = 'cart-recs';
        recsEl.id = 'cart-recommendations';
        recsEl.setAttribute('data-product-id', resolvedId);
        recsEl.setAttribute('aria-label', 'You may also like');
        recsEl.innerHTML = `
          <div class="cart-recs__header">
            <h3 class="cart-recs__heading">YOU MAY ALSO LIKE</h3>
            <div class="cart-recs__nav" id="cart-recs-nav" aria-label="Carousel navigation">
              <button class="cart-recs__nav-btn" id="cart-recs-prev" type="button" aria-label="Previous recommendations" disabled>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><polyline points="8,1 3,6 8,11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <button class="cart-recs__nav-btn" id="cart-recs-next" type="button" aria-label="Next recommendations">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><polyline points="4,1 9,6 4,11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </div>
          <div class="cart-recs__track-wrap">
            <div class="cart-recs__track" id="cart-recs-track" role="list"></div>
          </div>`;
        recsWrapper.appendChild(recsEl);
      }

      const productId = recsEl.dataset.productId;
      if (!productId) return;

      // Already loaded for this product — just re-init interactivity, no fetch
      if (this._cache[productId]) {
        const track = recsEl.querySelector('#cart-recs-track');
        if (track && track.querySelector('[data-rec-card]')) {
          CartRecommendations._initQuickAdd(recsEl);
          CartRecommendations._initCarouselNav(recsEl);
          return;
        }
      }

      this._loadRecommendations(productId, recsEl);
    },

    // Called at page load from a product page — fires the recommendations fetch
    // immediately so results are in _prefetchCache before the drawer ever opens.
    prefetch(productId) {
      // BUG FIX (recs — Bug 3): Always coerce to String so the cache key matches
      // the one produced by firstItem.dataset.productId (always a string) and by
      // the cart API product_id (a number). A mismatch caused the prefetch result
      // to be silently ignored and a redundant second fetch to fire on first open.
      productId = String(productId);
      if (!productId || this._prefetchCache[productId] || this._cache[productId]) return;
      this._prefetchCache[productId] = fetch(
        `/recommendations/products.json?product_id=${productId}&limit=10&intent=related`
      ).then(r => r.ok ? r.json() : Promise.reject()).catch(() => null);
    },

    async _loadRecommendations(productId, container) {
      productId = String(productId); // normalise — matches prefetch() cache key
      const track = container.querySelector('#cart-recs-track');
      if (!track) return;
      try {
        // Use the already-in-flight prefetch promise if available — zero extra
        // network round-trip, results arrive the moment the drawer opens.
        const pending = this._prefetchCache[productId];
        const data = pending ? await pending : await fetch(
          `/recommendations/products.json?product_id=${productId}&limit=10&intent=related`
        ).then(r => r.ok ? r.json() : Promise.reject());

        const products = (data?.products || []).filter(p => !isSetProduct(p));
        if (!products.length) {
          container.closest('.cart-drawer__recommendations')?.remove();
          return;
        }
        track.innerHTML = products.map(p => CartRecommendations._buildCardHTML(p)).join('');
        CartRecommendations._cache[productId] = true;
        CartRecommendations._initQuickAdd(container);
        CartRecommendations._initCarouselNav(container);
      } catch (err) {
        container.closest('.cart-drawer__recommendations')?.remove();
      }
    },

    _initCarouselNav(container) {
      const track   = container.querySelector('#cart-recs-track');
      const prevBtn = container.querySelector('#cart-recs-prev');
      const nextBtn = container.querySelector('#cart-recs-next');
      if (!track || !prevBtn || !nextBtn) return;

      const updateButtons = () => {
        const atStart = track.scrollLeft <= 4;
        const atEnd   = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        prevBtn.disabled = atStart;
        nextBtn.disabled = atEnd;
      };

      const scrollByPage = (dir) => {
        /* Read actual card width from CSS custom property for accuracy */
        const cardWidthPx = parseInt(
          getComputedStyle(document.documentElement)
            .getPropertyValue('--rec-card-width') || '136', 10
        );
        const gapPx = parseInt(
          getComputedStyle(document.documentElement)
            .getPropertyValue('--rec-card-gap') || '8', 10
        );
        /* Advance by 2 cards at a time */
        const step = (cardWidthPx + gapPx) * 2;
        track.scrollBy({ left: dir * step, behavior: 'smooth' });
      };

      prevBtn.addEventListener('click', () => scrollByPage(-1));
      nextBtn.addEventListener('click', () => scrollByPage(1));
      track.addEventListener('scroll', updateButtons, { passive: true });
      updateButtons();
    },

    /**
     * _buildCardHTML
     *
     * Builds the HTML for one recommendation card.
     *
     * SET PRODUCT HANDLING:
     *   For set/bundle products, we deduplicate the size pills so that only
     *   one button exists per unique size label. The first variant that matches
     *   each size is stored on the pill. When the customer picks "M", the
     *   correct bundle variant id (Top M + Pants M) is used for add-to-cart.
     *
     *   Detection uses isSetProduct() which mirrors the Liquid tag/title logic.
     *
     * IMAGE:
     *   Width 300 requested; height attr is fixed at 375 (4:5 ratio).
     *   CSS enforces object-fit:cover with a padding-top aspect ratio container,
     *   so no image can ever break the card layout.
     */
    _buildCardHTML(product) {
      const isComingSoon = isComingSoonProduct(product);

      const img = product.featured_image
        ? `<img
            class="cart-rec-card__image"
            src="${product.featured_image}"
            alt="${(product.title || '').replace(/"/g, '&quot;')}"
            loading="lazy"
            width="300"
            height="375"
           >`
        : `<div class="cart-rec-card__no-image"></div>`;

      const badgeHTML = isComingSoon
        ? `<div class="product-badges product-badges--top-left">
             <div class="product-badges__badge product-badges__badge--rectangle product-badges__badge--coming-soon">${(window.CartDrawerSettings?.comingSoonLabel || 'Coming Soon')}</div>
           </div>`
        : '';

      const variants = product.variants || [];
      const firstVariant = variants[0];

      /* Locate the Size option index */
      const sizeOptionIndex = (product.options || [])
        .findIndex(o => (o.name || o || '').toString().toLowerCase() === 'size');

      /* Build pills — deduplicated for sets. Never shown for coming-soon products. */
      let pillsHTML = '';

      if (sizeOptionIndex >= 0 && !isComingSoon) {
        const optionKey = `option${sizeOptionIndex + 1}`;
        const isSet = isSetProduct(product);

        if (isSet) {
          /* Unified selector: one pill per unique size value */
          const seenSizes = new Map(); /* size label → first matching variant */

          variants.forEach(v => {
            const sizeLabel = (v[optionKey] || '').toString();
            if (!sizeLabel || seenSizes.has(sizeLabel)) return;
            seenSizes.set(sizeLabel, v);
          });

          let isFirst = true;
          seenSizes.forEach((v, sizeLabel) => {
            const isAvail = v.available !== false;
            const selectedClass = isFirst ? ' cart-rec-card__variant-pill--selected' : '';
            const soldOutClass  = !isAvail ? ' cart-rec-card__variant-pill--sold-out' : '';
            pillsHTML += `
              <button
                class="cart-rec-card__variant-pill${selectedClass}${soldOutClass}"
                type="button"
                data-variant-id="${v.id}"
                data-variant-price="${v.price}"
                data-rec-variant-pill
                ${!isAvail ? 'disabled' : ''}
                aria-label="Size ${sizeLabel}"
                aria-pressed="${isFirst ? 'true' : 'false'}"
              >${sizeLabel}</button>`;
            isFirst = false;
          });

        } else {
          /* Standard product: one pill per available variant */
          const seenSizes = new Set();
          variants.forEach((v, idx) => {
            const sizeLabel = (v[optionKey] || '').toString();
            if (!sizeLabel || seenSizes.has(sizeLabel)) return;
            seenSizes.add(sizeLabel);
            const isAvail      = v.available !== false;
            const selectedClass = idx === 0 ? ' cart-rec-card__variant-pill--selected' : '';
            const soldOutClass  = !isAvail ? ' cart-rec-card__variant-pill--sold-out' : '';
            pillsHTML += `
              <button
                class="cart-rec-card__variant-pill${selectedClass}${soldOutClass}"
                type="button"
                data-variant-id="${v.id}"
                data-variant-price="${v.price}"
                data-rec-variant-pill
                ${!isAvail ? 'disabled' : ''}
                aria-label="Size ${sizeLabel}"
                aria-pressed="${idx === 0 ? 'true' : 'false'}"
              >${sizeLabel}</button>`;
          });
        }
      }

      const price = firstVariant ? CartUtils.formatMoney(firstVariant.price) : '';

      const addRowHTML = isComingSoon
        ? `<div class="cart-rec-card__add-row cart-rec-card__add-row--coming-soon">
             <span class="cart-rec-card__coming-soon-label">${(window.CartDrawerSettings?.comingSoonLabel || 'Coming Soon')}</span>
           </div>`
        : `<div class="cart-rec-card__add-row">
              <button class="cart-rec-card__qty-btn" type="button" data-rec-decrease aria-label="Decrease quantity">
                <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
                  <line x1="0" y1="1" x2="10" y2="1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
              <span class="cart-rec-card__qty-display" data-rec-qty>0</span>
              <button class="cart-rec-card__qty-btn" type="button" data-rec-increase aria-label="Add to cart">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <line x1="5" y1="0" x2="5" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="0" y1="5" x2="10" y2="5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>`;

      return `
        <div
          class="cart-rec-card"
          data-rec-card
          data-product-id="${product.id}"
          data-product-handle="${product.handle || ''}"
          data-selected-variant="${firstVariant?.id || ''}"
          data-coming-soon="${isComingSoon}"
          role="listitem"
        >
          <a href="${product.url}" class="cart-rec-card__image-link" aria-label="${(product.title || '').replace(/"/g, '&quot;')}">
            <div class="cart-rec-card__image-wrap">${badgeHTML}${img}</div>
          </a>
          <div class="cart-rec-card__body">
            <p class="cart-rec-card__title">${(product.title || '').toUpperCase()}</p>
            <p class="cart-rec-card__price" data-rec-price>${price}</p>
            ${pillsHTML ? `<div class="cart-rec-card__variants" role="group" aria-label="Select size">${pillsHTML}</div>` : ''}
            ${addRowHTML}
          </div>
        </div>`;
    },

    _initQuickAdd(container) {
      container.querySelectorAll('[data-rec-card]').forEach(card => {
        if (card._recInited) return;
        card._recInited = true;

        const qtyDisplay = card.querySelector('[data-rec-qty]');
        const priceEl    = card.querySelector('[data-rec-price]');
        let qty = 0;

        /* Variant pill selection */
        card.querySelectorAll('[data-rec-variant-pill]').forEach(pill => {
          pill.addEventListener('click', () => {
            /* Deselect all pills in this card */
            card.querySelectorAll('[data-rec-variant-pill]').forEach(p => {
              p.classList.remove('cart-rec-card__variant-pill--selected');
              p.setAttribute('aria-pressed', 'false');
            });
            /* Select clicked pill */
            pill.classList.add('cart-rec-card__variant-pill--selected');
            pill.setAttribute('aria-pressed', 'true');

            /* Update selected variant + price */
            card.dataset.selectedVariant = pill.dataset.variantId || '';
            if (priceEl && pill.dataset.variantPrice) {
              priceEl.textContent = CartUtils.formatMoney(pill.dataset.variantPrice);
            }
          });
        });

        /* Quantity decrease */
        card.querySelector('[data-rec-decrease]')?.addEventListener('click', () => {
          if (qty > 0) {
            qty--;
            qtyDisplay.textContent = qty;
          }
        });

        /* Quantity increase + add to cart */
        card.querySelector('[data-rec-increase]')?.addEventListener('click', async () => {
          const variantId = parseInt(card.dataset.selectedVariant, 10);
          if (!variantId) {
            /* Prompt user to select a size first */
            const variantsEl = card.querySelector('.cart-rec-card__variants');
            if (variantsEl) {
              variantsEl.style.outline = '1px solid #0a0a0a';
              setTimeout(() => { variantsEl.style.outline = ''; }, 1200);
            }
            return;
          }

          qty++;
          qtyDisplay.textContent = qty;

          try {
            // /cart/add.js returns the added line item — it includes the item's
            // final_price and the updated cart totals are in the response sections
            // if requested. Simplest zero-extra-fetch approach: use the line item
            // price from the response to update totals optimistically, then sync
            // the full cart in the background without blocking the UI.
            const lineItem = await CartAPI.addItem(variantId, 1);

            // Reset the quick-add counter immediately — before any awaits
            qty = 0;
            qtyDisplay.textContent = qty;

            // Open the drawer right away — user sees instant feedback
            window.cartDrawerInstance?.open();

            // Now fetch the real cart state and update everything in the background
            const cart = await CartAPI.getCart();

            const subtotalEl = document.querySelector(SELECTORS.subtotalAmount);
            if (subtotalEl) {
              subtotalEl.textContent = CartUtils.formatMoney(cart.total_price) + ' ' + cart.currency;
            }
            CartUtils.updateCartIcons(cart.item_count);
            CartUtils.updateShippingBar(cart.total_price);

            // Rebuild only the cart items list — recs section stays untouched
            const itemsContainer = document.querySelector(SELECTORS.itemsContainer);
            const footerEl = document.querySelector(SELECTORS.footer);
            if (itemsContainer && cart.item_count > 0) {
              itemsContainer.innerHTML = cart.items
                .map(item => CartUtils._buildItemHTML(item))
                .join('');
              if (footerEl) footerEl.style.display = '';
              CartQuantitySelector.init();
            }

          } catch (err) {
            qty = Math.max(0, qty - 1);
            qtyDisplay.textContent = qty;
            console.error('[CartRecs] quick add error:', err);
          }
        });
      });
    }
  };

  /* -------------------------------------------------------
     Boot
  ------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawerInstance = new CartDrawer();
    CartQuantitySelector.init();
    CartRecommendations.init();

    // PREFETCH: if we're on a product page, fire the recommendations request
    // immediately at page load — parallel with everything else — so that by
    // the time the customer clicks Add to Cart and the drawer opens, the fetch
    // is already complete (or nearly so) and renders instantly.
    //
    // Product id is read from the first [data-product-id] the theme stamps on
    // the page (present on product forms, product cards, etc.). Horizon also
    // exposes window.ShopifyAnalytics.meta.product.id on product pages.
    const prefetchId = (
      window.ShopifyAnalytics?.meta?.product?.id ||
      document.querySelector('[data-product-id]')?.dataset?.productId ||
      document.querySelector('form[action="/cart/add"] [name="id"]')
        ?.closest('[data-product-id]')?.dataset?.productId
    );
    if (prefetchId) {
      CartRecommendations.prefetch(String(prefetchId));
    }
  });

  window.CartAPI   = CartAPI;
  window.CartUtils = CartUtils;

})();
